import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Lock,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  UserPlus,
} from 'lucide-react';
import api from '../../services/api';
import PasswordSetForm from '../../components/PasswordSetForm';

const PortalResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const isSetup = searchParams.get('setup') === '1';
  const mode = isSetup ? 'setup' : 'reset';

  const [success, setSuccess] = useState(false);

  // F3: setup-mode self-recovery (request new invitation when token is missing)
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoverySubmitting, setRecoverySubmitting] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  const handleRequestNewInvitation = async (e) => {
    e.preventDefault();
    setRecoveryError('');
    setRecoverySubmitting(true);
    try {
      await api.post('/client-portal/auth/password-reset/request', {
        email: recoveryEmail,
      });
      setRecoverySent(true);
    } catch (err) {
      setRecoveryError(
        err.response?.data?.detail ||
          'Could not send invitation. Please try again.'
      );
    } finally {
      setRecoverySubmitting(false);
    }
  };

  const handleSubmit = async (newPassword) => {
    const result = await api.post('/client-portal/auth/password-reset/confirm', {
      token,
      new_password: newPassword,
    });

    if (result.data.auto_login) {
      localStorage.setItem('client_portal_token', result.data.access_token);
      localStorage.setItem(
        'client_portal_contact',
        JSON.stringify({
          contact_id: result.data.contact_id,
          contact_name: result.data.contact_name,
          email: result.data.email,
          intake_completed: result.data.intake_completed,
          is_first_login: result.data.is_first_login,
          profile_needs_update: result.data.profile_needs_update,
          pin_created: result.data.pin_created,
          must_change_password: result.data.must_change_password,
          staging_incomplete: result.data.staging_incomplete,
        })
      );
      // Route the user the same way PortalLogin.js does after a successful
      // login. Without this, first-time setup-link users skip profile-setup,
      // pin-setup, and staging entirely.
      const data = result.data;
      if (data.must_change_password) {
        navigate('/portal/change-password');
      } else if (data.staging_incomplete) {
        navigate('/portal/staging');
      } else if (data.is_first_login || data.profile_needs_update) {
        navigate('/portal/profile-setup');
      } else if (!data.pin_created) {
        navigate('/portal/pin-setup');
      } else if (!data.intake_completed) {
        navigate('/portal/intake');
      } else {
        navigate('/portal/dashboard');
      }
      return;
    }

    setSuccess(true);
    setTimeout(() => navigate('/portal'), 3000);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/20 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Link Incomplete</h2>
            <p className="text-virgo-teal-200 mb-6">
              {isSetup
                ? "This invitation link is incomplete. Please open the link directly from the original email — don't click 'back' or copy/paste fragments."
                : 'This password reset link is incomplete. Please open the link directly from the original email.'}
            </p>
            {isSetup ? (
              recoverySent ? (
                <>
                  <p className="text-green-300 mb-2">
                    If an account exists with that email, a new invitation has been sent. Please check your inbox.
                  </p>
                  <p className="text-virgo-teal-300/70 text-sm mt-4">
                    If you can't find the original email, contact your attorney's office.
                  </p>
                </>
              ) : (
                <>
                  <form onSubmit={handleRequestNewInvitation} className="space-y-3 text-left">
                    <input
                      type="email"
                      required
                      value={recoveryEmail}
                      onChange={(e) => setRecoveryEmail(e.target.value)}
                      placeholder="you@example.com"
                      disabled={recoverySubmitting}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-virgo-teal-300/50 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                    {recoveryError && (
                      <p className="text-red-300 text-sm">{recoveryError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={recoverySubmitting || !recoveryEmail}
                      className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {recoverySubmitting ? 'Sending…' : 'Send New Invitation'}
                    </button>
                  </form>
                  <p className="text-virgo-teal-300/70 text-sm mt-4">
                    If you can't find the original email, contact your attorney's office.
                  </p>
                </>
              )
            ) : (
              <Link
                to="/portal/forgot-password"
                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
              >
                Request New Link
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/20">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {isSetup ? 'Account Set Up!' : 'Password Reset!'}
              </h2>
              <p className="text-virgo-teal-200 mb-6">
                {isSetup
                  ? 'Your password has been set. Redirecting to login...'
                  : 'Your password has been successfully reset. Redirecting to login...'}
              </p>
              <Link
                to="/portal"
                className="inline-flex items-center gap-2 text-virgo-teal-300 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4" />
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 shadow-xl border border-white/20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/25">
              {isSetup ? (
                <UserPlus className="w-8 h-8 text-white" />
              ) : (
                <Lock className="w-8 h-8 text-white" />
              )}
            </div>
          </div>
          <PasswordSetForm mode={mode} onSubmit={handleSubmit} />
          <div className="mt-6 text-center">
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 text-virgo-teal-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
        <p className="text-center text-virgo-teal-300/60 text-sm mt-6">
          Client Portal
        </p>
      </div>
    </div>
  );
};

export default PortalResetPassword;
